import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { ActionIcon, Alert, Box, Button, Group, Skeleton, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconArrowsMinimize, IconChevronLeft, IconChevronRight, IconX, IconAlertCircle } from '@tabler/icons-react';
import { MenuItemDetails } from '../menu/MenuItemDetails';
import { MenuItem } from '../../types/menu';
import { getMenuItemDisplayText } from '../../utils/menuItemDisplayText';
import { GuideHit } from '../../utils/guideMatcher';
import { GuideExtras } from './GuideSheet';
import { FindOnMenu } from './FindOnMenu';
import { InfoState, ScanItemInfo, ScanLine } from './scanTypes';
import classes from './ScanSheet.module.css';

/** Notes from the ingredient / beef-cut guides about words in the line: extras, not what the line is. */
export interface ScanGuide {
  hits: GuideHit[];
  /** The whole menu is about another animal (cuts only) */
  menuAnimal: string | null;
}

interface ScanSheetProps {
  opened: boolean;
  /** Index of the line in the scan; the details view resets when it changes */
  index: number;
  line: ScanLine | null;
  state?: InfoState;
  guide?: ScanGuide;
  /** 1-based position of this line among all lines, and how many there are */
  position: { index: number; total: number };
  prev?: { label: string; onClick: () => void };
  next?: { label: string; onClick: () => void };
  onClose: () => void;
  onRetry?: () => void;
  askQuestion?: (info: ScanItemInfo, question: string) => Promise<string>;
  /** Google Lens look: floating rounded card with a "Minimize" pill above it instead of ✕ */
  lens?: boolean;
  /** The menu photo, for "Find on the menu" (above the questions) */
  photoUrl?: string | null;
  /** Shown under the printed line instead of looking it up: the beer scan fills the sheet from its guide, with no `state` */
  children?: React.ReactNode;
}

export interface ScanSheetHandle {
  /** Viewport y of the top of what the sheet covers when fully open (its Minimize pill included): a highlighted line must sit above it */
  coverTop: () => number;
}

const LANGUAGE = 'en';
const displayText = (item: MenuItem, field: 'name' | 'description' | 'disclaimer') => getMenuItemDisplayText(item, field, LANGUAGE);

/**
 * The bottom sheet for a tapped line, showing the item the way a SmartMenu v2
 * menu does (MenuItemDetails). Not modal: the photo stays live above it, so
 * tapping another line switches to it. Swipe down, ✕, or tap empty photo to close.
 */
export const ScanSheet = forwardRef<ScanSheetHandle, ScanSheetProps>(function ScanSheet(
  { opened, index, line, state, guide, prev, next, onClose, onRetry, askQuestion, lens = false, photoUrl, children },
  ref,
) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLElement | null>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const drag = useRef<{ y0: number; dy: number } | null>(null);

  useImperativeHandle(ref, () => ({
    coverTop() {
      const el = sheetRef.current;
      if (!el) return window.innerHeight;
      // The sheet's full height, not its current one: it's still sliding in, and grows when the item loads
      const cs = getComputedStyle(el);
      const max = parseFloat(cs.maxHeight);
      const top = window.innerHeight - (parseFloat(cs.bottom) || 0) - (Number.isFinite(max) ? max : el.offsetHeight);
      const pill = pillRef.current;
      return pill ? top - pill.offsetHeight - 12 : top;
    },
  }), []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [index]);

  // Swipe down to close, from the grip or when the body is scrolled to the top
  const onTouchStart = (e: React.TouchEvent) => {
    const fromGrip = (e.target as Element).closest(`.${classes.grip}`);
    drag.current = fromGrip || (bodyRef.current?.scrollTop ?? 0) <= 0 ? { y0: e.touches[0].clientY, dy: 0 } : null;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const d = drag.current, el = sheetRef.current;
    if (!d || !el) return;
    d.dy = Math.max(0, e.touches[0].clientY - d.y0);
    if (d.dy > 6) {
      el.classList.add(classes.dragging);
      el.style.transform = `translateY(${d.dy}px)`;
    }
  };
  const onTouchEnd = () => {
    const d = drag.current, el = sheetRef.current;
    if (!d || !el) return;
    el.classList.remove(classes.dragging);
    el.style.transform = '';
    if (d.dy > 80) onClose();
    drag.current = null;
  };

  const known = !!line?.match;
  const info = state?.status === 'ready' ? state.info : null;
  return (
    <aside
      ref={sheetRef}
      className={`${classes.sheet} ${lens ? classes.lens : ''} ${opened ? classes.open : ''}`}
      aria-label="What this line is"
      aria-hidden={!opened}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Lens: the pill floats just above the card's top right, outside it */}
      {lens && line && (
        <UnstyledButton ref={pillRef} className={classes.minimize} onClick={onClose}>
          <IconArrowsMinimize size={18} stroke={1.75} />
          <span>Minimize</span>
        </UnstyledButton>
      )}
      <button type="button" className={classes.grip} onClick={onClose} aria-label="Close" />
      <div ref={bodyRef} className={classes.body}>
        {line && (
          <>
            {/* Pinned to the top right of the sheet, so it stays put while the item scrolls */}
            {!lens && (
              <div className={classes.closeSlot}>
                <ActionIcon className={classes.close} variant="default" size={44} radius="xl" onClick={onClose} aria-label="Close">
                  <IconX size={20} />
                </ActionIcon>
              </div>
            )}
            {lens && (prev || next) && <Stepper prev={prev} next={next} top />}
            <Box className={classes.pad} pt={lens ? 4 : 44}>
              {/* Lens: the item's details repeat the printed name under the English title, so it's only shown until they load */}
              {(!lens || !info) && <Text className={classes.printed} lang="ja">{line.t}</Text>}

              {!children && (!state || state.status === 'loading') && <Loading known={known} />}
              {state?.status === 'error' && (
                <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md">
                  <Stack gap="xs" align="flex-start">
                    <Text size="sm">{state.error}</Text>
                    <Button size="compact-sm" variant="light" color="red" onClick={onRetry}>Try again</Button>
                  </Stack>
                </Alert>
              )}
              {info?.isMenuItem === false && (
                <Alert color="gray" variant="light" p="xs" mt="xs">
                  <Text size="sm">This doesn't look like a dish. Here's what it says:</Text>
                </Alert>
              )}
            </Box>

            {info && (
              <>
                <MenuItemDetails
                  key={index}
                  item={info.item}
                  getDisplayText={displayText}
                  restaurantName={info.restaurant?.name}
                  restaurantDescription={info.restaurant?.description}
                  currentLanguage={LANGUAGE}
                  askQuestion={askQuestion && ((q) => askQuestion(info, q))}
                >
                  {guide && <GuideExtras text={line.t} hits={guide.hits} menuAnimal={guide.menuAnimal} resetKey={index} />}
                  {photoUrl && <FindOnMenu photoUrl={photoUrl} line={line} />}
                </MenuItemDetails>
                <Box className={classes.pad}>
                  <Source line={line} info={info} />
                </Box>
              </>
            )}

            {children && <Box className={classes.pad}>{children}</Box>}

            {!lens && (prev || next) && <Stepper prev={prev} next={next} />}
          </>
        )}
      </div>
    </aside>
  );
});

type Step = { label: string; onClick: () => void };

/** Previous / next line. Lens: pinned to the top of the sheet; otherwise under the item. */
const Stepper: React.FC<{ prev?: Step; next?: Step; top?: boolean }> = ({ prev, next, top }) => (
  <Box className={`${classes.pad} ${top ? classes.stepperTop : ''}`}>
    <Group justify="space-between" wrap="nowrap" className={classes.stepper}>
      {prev ? (
        <UnstyledButton onClick={prev.onClick} className={classes.step} aria-label={`Previous: ${prev.label}`}>
          <IconChevronLeft size={16} />
          <Text size="sm" truncate lang="ja">{prev.label}</Text>
        </UnstyledButton>
      ) : <span />}
      {next && (
        <UnstyledButton onClick={next.onClick} className={`${classes.step} ${classes.stepNext}`} aria-label={`Next: ${next.label}`}>
          <Text size="sm" truncate lang="ja">{next.label}</Text>
          <IconChevronRight size={16} />
        </UnstyledButton>
      )}
    </Group>
  </Box>
);

const Loading: React.FC<{ known: boolean }> = ({ known }) => (
  <Stack gap="xs" aria-busy="true" pb="lg">
    <Text size="sm" c="dimmed">{known ? 'Looking it up…' : 'Working out what this is…'}</Text>
    <Skeleton height={26} width="70%" />
    <Skeleton height={14} width="40%" />
    <Skeleton height={14} mt="sm" />
    <Skeleton height={14} width="85%" />
  </Stack>
);

/** Where the item info came from: a stored menu item, or the AI. */
const Source: React.FC<{ line: ScanLine; info: ScanItemInfo }> = ({ line, info }) => {
  const match = line.match;
  return (
    <Text size="xs" c="dimmed" lh={1.5}>
      {info.source === 'known' && match ? (
        <>
          {match.how !== 'exact' && <>Matched <span lang="ja">“{match.name}”</span>. </>}
          Saved from earlier scans; the English was written by AI, so it can be wrong. Check with staff about allergies.
        </>
      ) : (
        <>Explained by AI from the menu text, so it can be wrong. Check with staff about allergies.</>
      )}
    </Text>
  );
};
